import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  PageHeader,
  DataTable,
  DataTableCard,
  Pagination2,
  Button,
  TableSkeleton,
} from '../../common';
import type { TableColumn } from '../../common/Table';
import { getSurveyTableColumns, getAudienceLabel } from './tableColumns';
import { useResponsesOverview } from './hooks/useResponsesOverview';
import { SurveyResponseSummary } from './types';
import { scrollToTop } from '../hooks';

/** Figma Survey Insights list: closed surveys with response rates (dc.html:2916). */
const ResponsesOverview: React.FC = () => {
  const navigate = useNavigate();

  const {
    surveys,
    surveysLoading,
    surveysError,
    statsError,
    totalSurveys,
    pagination,
    setPagination,
    refresh,
  } = useResponsesOverview();

  const handleViewInsights = (survey: SurveyResponseSummary) => {
    navigate(`/survey-responses/${survey.id}/insights`, {
      state: {
        title: survey.title,
        endDate: survey.endDate,
        audience: getAudienceLabel(survey),
      },
    });
    scrollToTop();
  };

  const columns = useMemo<TableColumn<SurveyResponseSummary>[]>(
    () => getSurveyTableColumns({ onViewInsights: handleViewInsights }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const hasError = statsError || surveysError;

  return (
    <div className="w-full">
      <PageHeader
        title="Survey Insights"
        description="Select a closed survey to review its responses"
        breadcrumbs={[{ label: 'Home', path: '/dashboard' }, { label: 'Survey Insights' }]}
      />

      {hasError ? (
        <div className="bg-[var(--color-bg-base)] border border-[var(--color-border-primary)] rounded-card shadow-[var(--shadow-sm)] text-center px-6 py-14">
          <h3 className="text-heading-sm text-[var(--color-text-primary)] mb-1">Failed to load responses</h3>
          <p className="text-body-sm text-[var(--color-text-secondary)] mb-5">
            Something went wrong while fetching surveys. Please try again.
          </p>
          <Button appearance="primary" size="small" onClick={() => refresh()}>
            Retry
          </Button>
        </div>
      ) : (
        <DataTableCard
          title="Closed Surveys"
          pagination={
            totalSurveys > pagination.limit ? (
              <Pagination2
                total={totalSurveys}
                page={pagination.page}
                pageSize={pagination.limit}
                pageSizeOptions={[5, 10, 20, 50]}
                onPageChange={(page) => setPagination({ ...pagination, page })}
                onPageSizeChange={(limit) => setPagination({ ...pagination, limit, page: 1 })}
              />
            ) : undefined
          }
        >
          <DataTable
            columns={columns}
            rows={surveys}
            rowKey={(r) => r.id}
            loading={surveysLoading}
            skeleton={<TableSkeleton rows={pagination.limit} columns={columns.length} />}
            emptyMessage="No surveys found"
            responsive="scroll"
            onRowClick={(record) => handleViewInsights(record)}
          />
        </DataTableCard>
      )}
    </div>
  );
};

export default ResponsesOverview;
